import type { Lead } from '@/types/crm'
import { useCopy } from '@/shared/ui/useCopy'

/**
 * The ten-second read, and nothing else.
 *
 * One paragraph saying what this company is, then the few facts that decide how
 * the conversation opens — certification, price, the tools they already run.
 * Laid out as a definition row rather than cards:
 * a rep scans left to right for the one they care about, and a grid of bordered
 * boxes makes that slower, not faster.
 *
 * A fact with no value is omitted entirely. An em-dash in a grid still costs a
 * column of attention to read and discard.
 */
export function LeadOverview({ lead, seatGap }: { lead: Lead; seatGap: boolean }) {
  const e = lead.enrichment
  const { copied, copy } = useCopy()

  /**
   * ---- Four facts came off this row on 4 Sep 2026 ----
   *
   * **Est. value** is the EST. ACV column, on by default, the same figure a
   * hand's width above this line. The column sorts and this could not.
   *
   * **Books with** and **Best window** were not used. Neither changed what
   * anybody did with a lead, and both cost a column of attention to read and
   * discard on every company opened.
   *
   * **Team** was not used *here* either — but the number behind it was worth
   * keeping, because how many guides a company has is how many seats a deal is
   * for. `scripts/seatsFromTeamSize.ts` copied it into `seatsExpected` on the
   * 77 leads where it came from real research, and Seats is now an editable
   * field on the panel. The estimate is still visible with its source in the
   * Sources disclosure, which is how anybody can see where a seat count came
   * from when nobody typed it.
   */
  const facts: [string, string][] = []
  if (e.certLevel) facts.push(['Certified', e.certLevel])
  if (e.pricePerPersonDayEur !== null) facts.push(['Price', `€${e.pricePerPersonDayEur}/day`])
  if (e.mapTools.length > 0) facts.push(['Map tools', e.mapTools.join(', ')])
  if (e.safetyTools.length > 0) facts.push(['Safety', e.safetyTools.join(', ')])
  // Registry facts, labelled so nobody mistakes payroll for guide count.
  if (e.employees) {
    facts.push([
      e.employeesYear ? `Employees (${e.employeesYear})` : 'Employees',
      e.employees,
    ])
  }
  if (e.revenueEur !== null) {
    facts.push([
      e.revenueYear ? `Revenue (${e.revenueYear})` : 'Revenue',
      `€${Math.round(e.revenueEur).toLocaleString('nb-NO')}`,
    ])
  }
  if (e.ceo) facts.push(['Managing director', e.ceo])
  if (e.foundedYear) facts.push(['Founded', String(e.foundedYear)])
  if (lead.seatsBought > 0) facts.push(['Seats bought', String(lead.seatsBought)])

  const nothing = !e.snapshot && facts.length === 0

  if (nothing) {
    return (
      <div className="ov ov--empty">
        Nothing harvested yet. The enrichment job fills in the team size, the tech stack and the
        outreach angle — until then this lead is a name and a domain.
      </div>
    )
  }

  return (
    <div className="ov">
      {e.snapshot && <p className="ov__snapshot">{e.snapshot}</p>}

      {/* The hook is the sentence a rep actually says. It was buried in the
          sources panel, which is the one place nobody opens before a call. */}
      {e.hook && (
        <div className="hook">
          <span className="hook__label">Angle</span>
          <p className="hook__text">{e.hook}</p>
          <button className="hook__copy" onClick={() => void copy('hook', e.hook)}>
            {copied === 'hook' ? '✓ copied' : 'copy'}
          </button>
        </div>
      )}

      {seatGap && (
        <p className="ov__seatgap">
          <strong>
            {lead.seatsActive} {lead.seatsActive === 1 ? 'person is' : 'people are'} already using
            Skida PRO here and nobody is paying.
          </strong>{' '}
          That is €{(lead.seatsActive * 360).toLocaleString('nb-NO')} a year on the table, and the
          roster below is who to name.
        </p>
      )}

      {facts.length > 0 && (
        <dl className="ov__facts">
          {facts.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      )}

      {e.employees && e.teamSize !== null && (
        <p className="ov__caveat ov__caveat--note">
          The register files {e.employees} employee{e.employees === '1' ? '' : 's'} against{' '}
          {e.teamSize} named guides. In guiding that gap is normal — freelancers do not appear on a
          payroll — and it is the guides who need seats, not the payroll.
        </p>
      )}

      {(e.verify || e.siteBlocked || e.confidence === 'low') && (
        <p className="ov__caveat">
          {e.siteBlocked
            ? 'Their site refused to load, so this was pieced together from registries and social.'
            : e.confidence === 'low'
              ? 'Low confidence — one axis or fewer has a sourced signal. Read the sources before acting on the score.'
              : 'Contains an inferred value. Worth a glance at the sources before outreach.'}
        </p>
      )}
    </div>
  )
}
